import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Radio,
  Select,
  Input,
  Empty,
  Tabs,
  message,
  Checkbox,
} from "antd";
import api from "../../../../api";
import { useAuth } from "../../../../store/context/AuthContext";
import CustomModal from "../../../../components/modal/CustomModal";
import CustomInput from "../../../../components/input/CustomInput";
import { formatDateTime } from "../../../../utilities/utils";
import { useToast } from "../../../../components/toast/toast";
import ConfirmModal from "../../../../components/modal/ConfirmModal";

const QUALIFY_OPTIONS = [
  { value: "GOOD", label: "Сонирхолтой (GOOD)" },
  { value: "AVERAGE", label: "Дундаж (AVERAGE)" },
  { value: "BAD", label: "Сонирхолгүй (BAD)" },
  { value: "NO_INTEREST", label: "Огт сонирхолгүй" },
  { value: "BUSY", label: "Завгүй" },
  { value: "NEXT", label: "Эргэн холбогдох" },
];

const OUTCOME_OPTIONS = [
  { value: "ANSWERED", label: "Холбогдсон" },
  { value: "NO_ANSWER", label: "Авсангүй" },
  { value: "BUSY", label: "Завгүй" },
  { value: "FAILED", label: "Алдаа (Failed)" },
];

const BUCKETS = [
  { key: "UNATTEMPED", label: "Оролдлого хийгээгүй" },
  { key: "NO_ANSWER", label: "Дуудлага аваагүй" },
  { key: "BUSY", label: "Завгүй" },
];

// backend руу UNATTEMPED → UNATTEMPTED(normalize)
const normalizeForBE = (b) => {
  const x = String(b || "").toUpperCase();
  if (["NO_ANSWER", "BUSY"].includes(x)) return x;
  return "UNATTEMPTED";
};

export default function OperatorPanel({ listId, setRefreshKey }) {
  const { user } = useAuth();
  const agentId = user?.id || "";
  const toast = useToast();

  const [activeBucket, setActiveBucket] = useState("UNATTEMPED");
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);

  // attempt form
  const [outcome, setOutcome] = useState("");
  const [qualify, setQualify] = useState(null);
  const [notes, setNotes] = useState("");

  // SMS modal
  const [smsText, setSmsText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [isLoadSms, setIsLoadSms] = useState(false);

  // PIN
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [pinned, setPinned] = useState(false);

  const submitDisabled =
    !target || !outcome || (outcome === "ANSWERED" && !qualify);

  const resetForm = () => {
    setOutcome("");
    setQualify(null);
    setNotes("");
  };

  // === Core: get-or-claim (POST /current) ===
  const fetchTarget = async () => {
    if (!listId || !agentId || !activeBucket) return;
    setLoading(true);

    const res = await api.get_current_or_claim({
      list_id: listId,
      agent_id: agentId,
      outcome: normalizeForBE(activeBucket),
      pinned_only: pinnedOnly,
    });
    if (res.status === 200) {
      const data = res?.data?.data;
      // console.log("target data -->", data);
      setNotes(data?.Attempts?.[0]?.notes || "");
      setTarget(data);
      setPinned(!!data?.pin);
    } else {
      setTarget(null);
      toast(res.data.message || "Алдаа гарлаа", "error");
    }
    setLoading(false);
  };

  // Таб солигдох/анх ачаалахад
  useEffect(() => {
    fetchTarget();
  }, [listId, agentId, activeBucket, pinnedOnly]);

  // === Submit → Next ===
  const submitAndNext = async () => {
    if (!target) return;
    setLoading(true);
    try {
      // 1) attempt хадгал
      await api.create_attempt({
        list_id: listId,
        target_id: target.id,
        agent_id: agentId,
        outcome,
        qualify: outcome === "ANSWERED" ? qualify : undefined,
        notes,
      });

      // 2) дараагийг lock-лоод харуул
      const resNext = await api.claim_next_target({
        list_id: listId,
        agent_id: agentId,
        outcome: normalizeForBE(activeBucket),
        pinned_only: pinnedOnly,
      });
      resetForm();
      const next = resNext?.data?.data || null;
      setNotes(next?.Attempts?.[0]?.notes || "");
      setPinned(!!next?.pin);
      setTarget(next);
      if (!next)
        toast("Дараагийн дугаар олдсонгүй (энэ таб дууссан байх магад).");

      // гаднах лист/статыг сэргээх
      setRefreshKey((prev) => prev + 1);
    } catch (e) {
      // console.error(e);
      message.error("Хадгалах үед алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const sendSmsHandler = async () => {
    if (target?.phone && target?.id) {
      setIsLoadSms(true);
      const res = await api.call_center_sms({
        targetId: target?.id || "",
        phone: target.phone || "",
        smsText,
      });
      if (res.status === 200) {
        toast("Амжилттай илгээлээ", "success");
        setShowModal(false);
        setIsConfirmModal(false);
        setSmsText("");
      } else {
        toast(res.data.message || "Алдаа гарлаа", "error");
      }
      setIsLoadSms(false);
    }
  };

  const tabItems = BUCKETS.map((b) => ({ key: b.key, label: b.label }));

  const togglePin = async (checked) => {
    if (!target?.id) return;
    setPinned(checked);
    try {
      await api.call_center_toggle_pin({
        target_id: target.id,
        agent_id: agentId,
        pin: checked,
      });
      // Пинлэсэн бол гаднах статистикт өөрчлөлт байхгүй — refresh шаардлагагүй
    } catch (e) {
      setPinned(!checked); // rollback
      message.error("Тэмдэглэх үед алдаа гарлаа");
    }
  };

  return (
    <>
      <Tabs
        items={tabItems}
        activeKey={activeBucket}
        onChange={(k) => setActiveBucket(k)}
        style={{ marginBottom: 16 }}
      />

      {activeBucket !== "UNATTEMPED" && (
        <Checkbox
          checked={pinnedOnly}
          onChange={(e) => setPinnedOnly(e.target.checked)}
          style={{ marginBottom: 16, width: "max-content" }}
        >
          Тэмдэглэсэн дугаарууд харах
        </Checkbox>
      )}

      <Flex
        style={{
          width: "max-content",
          border: "1px solid #eee",
          borderRadius: "10px",
          padding: "30px",
          backgroundColor: "#f9f9f9",
        }}
      >
        {!target ? (
          <Flex vertical align="center" gap={12}>
            <Empty
              description="Дугаар олдсонгүй"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Flex>
        ) : (
          <Flex vertical gap={20} style={{ width: 420 }}>
            {(target?.attempts_count > 0 || target?.last_called_at) && (
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px solid #ddd",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                }}
              >
                <div style={{ color: "#666" }}>
                  Нийт оролдлого: <b>{target?.attempts_count || 0}</b>
                </div>
                {target?.last_called_at && (
                  <div style={{ color: "#666", marginTop: 4 }}>
                    Сүүлийн үйлдэл:{" "}
                    <b>{formatDateTime(target?.last_called_at)}</b>
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ marginBottom: 6 }}>Утасны дугаар</div>
                <div style={{ fontSize: 26, fontWeight: 600 }}>
                  {target.phone}
                </div>
              </div>

              <Checkbox
                checked={pinned}
                onChange={(e) => togglePin(e.target.checked)}
              >
                Тэмдэглэх
              </Checkbox>
            </div>

            <div>
              <div style={{ marginBottom: 6 }}>Дуудлагын төлөв</div>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                options={OUTCOME_OPTIONS}
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              />
            </div>

            <div>
              <div style={{ marginBottom: 6 }}>Хэрэглэгчийн төлөв</div>
              <Select
                allowClear
                placeholder="Сонгоно уу"
                value={qualify}
                onChange={setQualify}
                style={{ width: "100%", minHeight: 40 }}
                options={QUALIFY_OPTIONS}
                disabled={outcome !== "ANSWERED"}
              />
            </div>

            <div>
              <div style={{ marginBottom: 6 }}>Тэмдэглэл</div>
              <Input.TextArea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <Flex gap={8} align="center" justify="space-between">
              <Button
                type="dashed"
                style={{ height: 40, padding: "0 30px" }}
                onClick={() => setShowModal(true)}
                disabled={!target || !target.phone}
              >
                Мессеж илгээх
              </Button>

              <Button
                type="primary"
                style={{ height: 40, padding: "0 30px" }}
                onClick={submitAndNext}
                disabled={submitDisabled}
                loading={loading}
              >
                Хадгалаад дараагийнх
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>

      <CustomModal
        open={showModal}
        title="Мессеж илгээх"
        onCancel={() => {
          setShowModal(false);
          setSmsText("");
        }}
        footer={
          <Button
            type="primary"
            style={{ height: 40, padding: "0 30px" }}
            onClick={() => {
              // console.log("send sms:", smsText);
              // setShowModal(false);
              setIsConfirmModal(true);
            }}
            disabled={!smsText || !target}
          >
            Илгээх
          </Button>
        }
      >
        <CustomInput
          required
          title="Илгээх мессеж"
          value={smsText}
          onChange={(e) => setSmsText(e.target.value)}
        />
      </CustomModal>

      <ConfirmModal
        variant="warning"
        title="Итгэлтэй байна уу?"
        description={"Энэ үйлдлийг буцааж болохгүй тул сайтар нягтална уу!"}
        showModal={isConfirmModal}
        setShowModal={setIsConfirmModal}
        submitHandler={sendSmsHandler}
        disabled={isLoadSms}
        loading={isLoadSms}
      />
    </>
  );
}
